import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useCustomerFetch, SectionLabel } from "../../components/customer-app/ui";
import CustomerPhotoPicker from "../../components/customer-app/CustomerPhotoPicker";
import { Cpu, AlertCircle, ChevronLeft, Loader2 } from "lucide-react";

const CATEGORIES = ["Inverter fault", "Low generation", "Physical damage", "Billing query", "Other"];

export default function CustomerRaiseComplaint() {
  const nav = useNavigate();
  const { data: ibs } = useCustomerFetch("/customer-app/installed-base");

  const [mode, setMode] = useState("equipment"); // 'equipment' | 'general'
  const [ibId, setIbId] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const { data: ibDetail } = useCustomerFetch(ibId ? `/customer-app/installed-base/${ibId}` : null, [ibId]);

  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const equipmentOptions = useMemo(() => ibDetail?.equipment || [], [ibDetail]);

  async function submit(e) {
    e.preventDefault();
    if (mode === "equipment" && !ibId) { toast.error("Select which installation this is about"); return; }
    setSubmitting(true);
    try {
      const { data: ticket } = await api.post("/customer-app/tickets", {
        installed_base_id: mode === "equipment" ? ibId : undefined,
        equipment_id: mode === "equipment" ? equipmentId || undefined : undefined,
        title: category,
        description,
        priority,
      });
      if (photos.length) {
        for (const f of photos) {
          const form = new FormData();
          form.append("file", f);
          await api.post(`/service-tickets/${ticket.id}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
        }
      }
      toast.success("Complaint raised — our team will reach out shortly!");
      nav("/app/tickets");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  }

  return (
    <div className="pb-4" data-testid="customer-raise-complaint">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-3">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>
      <h2 className="text-xl font-extrabold tracking-tight text-slate-900">Raise a Complaint</h2>
      <p className="text-sm text-slate-500 mt-1">Tell us what's wrong — we'll get on it.</p>

      <form onSubmit={submit} className="space-y-5 mt-5">
        <div>
          <SectionLabel className="mb-2">This complaint is about</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMode("equipment")} data-testid="complaint-mode-equipment"
              className={"py-3 rounded-xl text-sm font-semibold border-2 transition-colors " + (mode === "equipment" ? "border-[#0D4721] bg-emerald-50 text-[#0D4721]" : "border-slate-200 text-slate-500")}>
              <Cpu className="w-4 h-4 inline mr-1.5" /> Specific Equipment
            </button>
            <button type="button" onClick={() => setMode("general")} data-testid="complaint-mode-general"
              className={"py-3 rounded-xl text-sm font-semibold border-2 transition-colors " + (mode === "general" ? "border-[#0D4721] bg-emerald-50 text-[#0D4721]" : "border-slate-200 text-slate-500")}>
              <AlertCircle className="w-4 h-4 inline mr-1.5" /> General Issue
            </button>
          </div>
        </div>

        {mode === "equipment" && (
          <div className="space-y-3">
            <div>
              <SectionLabel className="mb-2">Which installation?</SectionLabel>
              <select value={ibId} onChange={(e) => { setIbId(e.target.value); setEquipmentId(""); }} data-testid="complaint-ib-select"
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-400">
                <option value="">— Select installation —</option>
                {(ibs || []).map((ib) => <option key={ib.id} value={ib.id}>{ib.ib_number} · {ib.equipment_count} equipment</option>)}
              </select>
            </div>
            {ibId && (
              <div>
                <SectionLabel className="mb-2">Which equipment? <span className="text-slate-400">(optional)</span></SectionLabel>
                <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} data-testid="complaint-equipment-select"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-400">
                  <option value="">— Any / not sure —</option>
                  {equipmentOptions.map((eq) => <option key={eq.id} value={eq.id}>{eq.material_name} · {eq.serial_code}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        <div>
          <SectionLabel className="mb-2">Issue category</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => setCategory(c)} data-testid={`complaint-category-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={"px-3 py-1.5 rounded-full text-xs font-bold border transition-colors " + (category === c ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200")}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel className="mb-2">Describe the issue</SectionLabel>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} required rows={4}
            placeholder="What's happening? When did you notice it?"
            data-testid="complaint-description"
            className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-400" />
        </div>

        <div>
          <SectionLabel className="mb-2">Priority</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            {["normal", "urgent"].map((p) => (
              <button key={p} type="button" onClick={() => setPriority(p)} data-testid={`complaint-priority-${p}`}
                className={"py-2.5 rounded-xl text-sm font-semibold border-2 capitalize transition-colors " + (priority === p ? "border-[#0D4721] bg-emerald-50 text-[#0D4721]" : "border-slate-200 text-slate-500")}>
                {p}
              </button>
            ))}
          </div>
        </div>

        <CustomerPhotoPicker files={photos} onChange={setPhotos} />

        <button type="submit" disabled={submitting} data-testid="complaint-submit"
          className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2 disabled:opacity-40">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Complaint
        </button>
      </form>
    </div>
  );
}
